import { useQuery } from '@apollo/client';
import {
    BudgetDocument,
    BudgetFieldsFragment,
    BudgetPurpose,
} from '@common/graphql/generated';
import {
    BUDGET_ONBOARDING_STEPS,
    BudgetFormVariant,
    BudgetOnboardingStep,
    useAreTransactionsSynced,
} from '@common/react/budget';
import { createContext, useContext, useState } from 'react';
import { useNextBudgetOnboardingStep } from './useNextBudgetOnboardingStep';

export const BUDGET_PURPOSE_TO_STEP_MAP: Record<
    BudgetPurpose,
    BudgetOnboardingStep
> = {
    fixed: 'fixed-expenses',
    lifestyle: 'lifestyle-expenses',
    income: 'income',
    saving_investing: 'saving-investing',
};

interface BudgetOnboardingContext {
    currentStep: BudgetOnboardingStep;
    setCurrentStep: (currentStep: BudgetOnboardingStep) => void;
    goToNextStep: () => void;
    goToPreviousStep: () => void;
    visitedSteps: Set<BudgetOnboardingStep>;
    budget: BudgetFieldsFragment | undefined | null;
    budgetLoading: boolean;
    variant: BudgetFormVariant;
    autofocusGroupId?: string;
}

const Ctx = createContext<BudgetOnboardingContext>({
    currentStep: 'link-accounts',
    setCurrentStep: () => {},
    goToNextStep: () => {},
    goToPreviousStep: () => {},
    budget: undefined,
    budgetLoading: false,
    visitedSteps: new Set(),
    variant: 'create',
    autofocusGroupId: undefined,
});

export const BudgetOnboardingProvider = ({
    variant,
    children,
    initialStep,
    autofocusGroupId,
}: {
    variant: BudgetFormVariant;
    initialStep?: BudgetOnboardingStep;
    children: React.ReactNode;
    autofocusGroupId?: string;
}) => {
    const areTransactionsSynced = useAreTransactionsSynced();
    const nextStep = useNextBudgetOnboardingStep();
    const [currentStep, setCurrentStep] = useState<BudgetOnboardingStep>(
        initialStep || nextStep
    );
    const [visitedSteps, setVisitedSteps] = useState<Set<BudgetOnboardingStep>>(
        new Set([initialStep || nextStep])
    );
    const { data: budgetData, loading: budgetLoading } =
        useQuery(BudgetDocument);

    const setCurrentStepAndTrack = (step: BudgetOnboardingStep) => {
        setVisitedSteps((prev) => new Set([...prev, step]));
        setCurrentStep(step);
    };

    const goToNextStep = () => {
        const currentIndex = BUDGET_ONBOARDING_STEPS.indexOf(currentStep);
        let nextStep = BUDGET_ONBOARDING_STEPS[currentIndex + 1];

        // Skip the transactions-syncing step if transactions are already synced
        if (nextStep === 'transactions-syncing' && areTransactionsSynced) {
            nextStep = BUDGET_ONBOARDING_STEPS[currentIndex + 2];
        }

        if (nextStep) {
            setCurrentStepAndTrack(nextStep);
        }
    };

    const goToPreviousStep = () => {
        const currentIndex = BUDGET_ONBOARDING_STEPS.indexOf(currentStep);
        const previousStep = BUDGET_ONBOARDING_STEPS[currentIndex - 1];

        if (previousStep) {
            setCurrentStepAndTrack(previousStep);
        }
    };

    return (
        <Ctx.Provider
            value={{
                currentStep,
                setCurrentStep: setCurrentStepAndTrack,
                goToNextStep,
                goToPreviousStep,
                visitedSteps,
                budget: budgetData?.currentUserBudget,
                budgetLoading,
                variant,
                autofocusGroupId,
            }}
        >
            {children}
        </Ctx.Provider>
    );
};

export const useBudgetOnboardingContext = (): BudgetOnboardingContext => {
    return useContext(Ctx);
};
